/** Dashboard widget (v1.3.0, Part 8): the most recent probabilistic forecasts platform-wide. */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { RecentForecast } from "@/types";
import WidgetCard from "./WidgetCard";

const TWELVE_MONTH_DAYS = 252;

export default function ForecastSummaryWidget(): React.ReactElement {
  const [forecasts, setForecasts] = useState<RecentForecast[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<RecentForecast[]>("/forecasts/recent", { params: { limit: 5 } })
      .then(({ data }) => !cancelled && setForecasts(Array.isArray(data) ? data : []))
      .catch(() => !cancelled && setError("Unable to load forecasts"));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <WidgetCard title="Forecast summary" isLoading={forecasts === null && !error} error={error}>
      {forecasts && forecasts.length > 0 ? (
        <ul className="space-y-2">
          {forecasts.map((entry) => {
            const yearOut = entry.forecasts.find(
              (horizon) => horizon.horizon_days === TWELVE_MONTH_DAYS
            );
            const expected = yearOut ? Number(yearOut.expected_return_pct) : null;
            return (
              <li key={`${entry.company_id}-${entry.generated_at}`}>
                <Link
                  to={`/research/${entry.company_id}`}
                  className="flex items-center justify-between text-sm hover:text-mist-100"
                >
                  <span className="font-mono text-signal-teal">{entry.symbol}</span>
                  {expected === null ? (
                    <span className="text-mist-400">—</span>
                  ) : (
                    <span
                      className={`font-mono ${expected >= 0 ? "text-signal-teal" : "text-signal-rose"}`}
                      title="12-month expected return (probabilistic, not guaranteed)"
                    >
                      {expected >= 0 ? "+" : ""}
                      {expected.toFixed(1)}% 12M
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-mist-400">
          Generate a forecast on any company report to see it summarized here.
        </p>
      )}
    </WidgetCard>
  );
}
